/** @format */

import {useMemo} from 'react';

import {differenceInDays, format, subDays} from 'date-fns';

import {getSpentSum} from 'pages/Home/Main/Project/components/BudgetAndExpese/components/ExpenditureWidget/helpers/getSpentSum';

import {useBudgetWidgetsData} from '../../../hooks/BudgetWidgetsDataContext';
import {useExpenditure} from './useExpenditure';

export interface YesterdayExpenditureStats {
  yesterdaySpent: number;
  dailyPlan: number;
  hasPlan: boolean;
  currency: string;
  difference: number;
  percentageDeviation: number;
  isOverspent: boolean;
  yesterdayDate: string;
  dayBeforeYesterdaySpent: number;
  comparisonDifference: number;
  isGrowth: boolean;
}

export interface YesterdayExpenditureReturn {
  result: YesterdayExpenditureStats;
  error: boolean;
  loading: boolean;
}

export const useYesterdayExpenditure = (): YesterdayExpenditureReturn => {
  const {today, yesterday} = useMemo(() => {
    const todayDate = new Date();
    const todayString = format(todayDate, 'yyyy-MM-dd');
    const normalizedToday = new Date(todayString);
    const yesterdayDate = subDays(normalizedToday, 1);
    return {
      today: normalizedToday,
      yesterday: yesterdayDate,
    };
  }, []);

  const budgetWidgetsData = useBudgetWidgetsData();
  const contextCompany = budgetWidgetsData?.data?.currentUser?.currentCompany;
  const contextYesterdayStats = contextCompany?.yesterdayStats ?? null;
  const contextDayBeforeYesterdayStats = contextCompany?.dayBeforeYesterdayStats ?? null;

  const {
    result: generalExpenditure,
    loading: generalLoading,
    error: generalError,
  } = useExpenditure();

  const result: YesterdayExpenditureStats = useMemo(() => {
    const yesterdayStats = contextYesterdayStats;
    const dayBeforeYesterdayStats = contextDayBeforeYesterdayStats;

    const yesterdaySpent = getSpentSum(yesterdayStats);
    const dayBeforeYesterdaySpent = getSpentSum(dayBeforeYesterdayStats);

    const currency = yesterdayStats?.find(stat => stat.currency)?.currency || 'RUB';

    let dailyPlan = 0;
    let hasPlan = false;

    if (generalExpenditure.type === 'mediaplan' && generalExpenditure.plan > 0) {
      const remainingBudget = generalExpenditure.plan - generalExpenditure.spent;

      if (remainingBudget > 0) {
        // Включаем текущий день в расчёт и зажимаем минимум 1 день
        const rawDays = differenceInDays(generalExpenditure.endDateFull ?? new Date(), today) + 1;
        const remainingDays = Math.max(1, rawDays);
        dailyPlan = remainingBudget / remainingDays;
        hasPlan = true;
      }
    }

    const difference = dailyPlan - yesterdaySpent;
    const percentageDeviation =
      dailyPlan > 0 ? ((yesterdaySpent - dailyPlan) / dailyPlan) * 100 : 0;

    const comparisonDifference = yesterdaySpent - dayBeforeYesterdaySpent;
    const isGrowth = comparisonDifference > 0;

    return {
      yesterdaySpent,
      dailyPlan,
      hasPlan,
      currency,
      difference: Math.abs(difference),
      percentageDeviation,
      isOverspent: difference < 0,
      yesterdayDate: format(yesterday, 'dd.MM.yyyy'),
      dayBeforeYesterdaySpent,
      comparisonDifference: Math.abs(comparisonDifference),
      isGrowth,
    };
  }, [contextYesterdayStats, contextDayBeforeYesterdayStats, generalExpenditure, today, yesterday]);

  return {
    result,
    loading: Boolean(budgetWidgetsData?.loading) || generalLoading,
    error: Boolean(budgetWidgetsData?.error) || generalError,
  };
};
